// client/src/routes/app/expenses/$expenseId.edit.tsx — SCR-EXP-002 (edit) · EXP-005 (04 §8.7).
//
// A flat file rather than a `$expenseId/` directory, which is the path 04 §9.1's own tree prints
// (`app/expenses/$expenseId.edit.tsx`) and the honest one here: `properties/$propertyId/` and
// `contracts/$contractId/` are directories because they hold a layout route with tabs under it,
// and an expense has no detail screen to be the layout of.
//
// `Expenses.Write` — editing a past expense is allowed and audited (05 EXP-001 criterion 3,
// D-685), so nothing here refuses an old date; the trail records the diff.
//
// IT EDITS EITHER ARM OF THE XOR (OAT-003, D-1196). `GET /api/v1/expenses/{id}` answers for a
// personal cost too — `GetExpenseQueryHandler` falls through to `PersonalDetailAsync` — so this
// URL is reachable for one, by hand or by a stale link. Before OAT-003 it rendered the property
// form against it: the property select had nothing to select, `expenseSchema` refused with
// „Câmp obligatoriu" on a field the cost does not have, and no save was possible. The detail
// says which arm it is, so the form is locked to that arm and the correct payload is sent.
//
// The Owner-as-Tenant section does NOT link here. It edits a personal cost in place, so the
// landlord stays inside „Chiriaș la rândul meu" (D-1191); this is the deep link's path, and it
// carries 04 §5.8's banner because the form does.
//
// IT IS ALSO WHERE A RECEIPT IS REPAIRED (D-2212). This is the one screen that holds an expense
// id without having to create one, so the vault's panel sits under the form: it lists what is
// already filed against the cost, takes an upload that needs no ordering at all, and is what the
// sentence after a failed attach on „cheltuială nouă" sends the landlord to. The form's own
// control still works here too — a bon picked while correcting an amount is filed after the
// `PUT`, from the id this route already has.

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import type { ExpenseDetail } from '@/client';
import { PageHeader } from '@/components/layout/PageHeader';
import { RequirePermission } from '@/components/layout/route-guards';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { ExpenseDocumentsPanel } from '@/features/expenses/ExpenseDocumentsPanel';
import { ExpenseForm } from '@/features/expenses/ExpenseForm';
import { useExpense, useUpdateExpense } from '@/features/expenses/queries';
import { useAttachReceipt, type ReceiptDraft } from '@/features/expenses/receipt';
import {
  toExpenseForm,
  toExpenseInput,
  toPersonalExpenseInput,
  type ExpenseFormValues,
} from '@/features/expenses/schema';
import { toast } from '@/stores/toast-store';

export const Route = createFileRoute('/app/expenses/$expenseId/edit')({
  component: EditExpenseScreen,
});

function EditExpenseScreen() {
  const { t } = useTranslation();
  const { expenseId } = Route.useParams();
  const navigate = useNavigate();
  const expense = useExpense(expenseId);
  const update = useUpdateExpense();
  const attachReceipt = useAttachReceipt();

  // The way back follows `landing`: the register for a property cost, the Owner-as-Tenant
  // section for a personal one. Unknown until the detail says which arm it is.
  const personal = expense.isSuccess && expense.data.scope === 'PERSONAL';

  return (
    <RequirePermission permission="Expenses.Write">
      <PageHeader
        title={t('expenses.form.editTitle')}
        description={t('expenses.form.editLead')}
        backTo={expense.isSuccess ? (personal ? '/app/owner-tenancy' : '/app/expenses') : undefined}
        backLabel={
          expense.isSuccess
            ? personal
              ? t('nav.ownerTenancy')
              : t('expenses.list.title')
            : undefined
        }
      />

      {expense.isPending ? <LoadingState rows={3} /> : null}
      {expense.isError ? (
        <ErrorState error={expense.error} onRetry={() => void expense.refetch()} />
      ) : null}

      {expense.isSuccess ? (
        <EditForm
          detail={expense.data}
          onSubmit={async (values: ExpenseFormValues, receipt: ReceiptDraft | null) => {
            const body =
              expense.data.scope === 'PERSONAL'
                ? toPersonalExpenseInput(values)
                : toExpenseInput(values);
            await update.mutateAsync({ path: { id: expenseId }, body });
            toast.success(t('expenses.form.updated'));
            await attachReceipt(expenseId, receipt);
            await navigate(landing(expense.data));
          }}
          onCancel={() => {
            void navigate(landing(expense.data));
          }}
        />
      ) : null}

      {expense.isSuccess ? (
        <ExpenseDocumentsPanel expenseId={expenseId} label={expense.data.descriptionRo} />
      ) : null}
    </RequirePermission>
  );
}

/**
 * Where „Renunță" and a successful save go back to. A property cost belongs to the register it
 * came from; a personal one belongs to its rental's section, with that rental selected — leaving
 * a landlord on the portfolio ledger after editing a personal receipt would be exactly the
 * confusion the banner exists to prevent.
 */
function landing(detail: ExpenseDetail) {
  return detail.scope === 'PERSONAL' && detail.ownerTenancyId != null
    ? ({
        to: '/app/owner-tenancy',
        search: { tenancy: detail.ownerTenancyId, tab: 'ledger' as const },
      } as const)
    : ({ to: '/app/expenses' } as const);
}

function EditForm({
  detail,
  onSubmit,
  onCancel,
}: {
  detail: ExpenseDetail;
  /** Passed through to `ExpenseForm`, receipt and all — see its own prop for why (D-2212). */
  onSubmit: (values: ExpenseFormValues, receipt: ReceiptDraft | null) => Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useTranslation();

  return (
    <ExpenseForm
      initialValues={toExpenseForm(detail)}
      verdict={detail}
      submitLabel={t('common.save')}
      onSubmit={onSubmit}
      onCancel={onCancel}
      scope={detail.scope}
      // D-235: the serializer omits a null property entirely, so `??` and never `=== null`.
      tenancyLabel={detail.ownerTenancyLabel ?? ''}
    />
  );
}
